import { typeForDisplay } from '../utils/type-utils'
import safeJsonStringify from 'safe-json-stringify'

// When adding new messages, update Support article with error code and explanation about the error.
export const messages = {
  collectionNameMustBeAString: () =>
    'WDE0001: Collection name must be a string.',
  itemIdMustBeAString: () => 'WDE0002: ItemId must be a string.',
  itemIdsMustBeArrayOfStrings: () =>
    'WDE0068: Item ids must be an array of strings',
  removeItemsMustBeLessThanThousand: (collectionName: string) =>
    `WDE0069: Failed to remove items from [${collectionName}].\nCannot remove more than 1000 items in one request`,
  fieldNameMustBeAString: () => 'WDE0003: FieldName must be a string.',
  itemMustBeAnObject: (item: any, collectionName: string) =>
    `WDE0004: Failed to save [${item}] into [${collectionName}].\nItems must be JavaScript objects.`,
  itemsMustBeArrayOfObjects: (collectionName: string) =>
    `WDE0005: Failed to bulk save items into [${collectionName}].\nItems must be an array of JavaScript objects and itemIds must be strings if present.`,
  itemsMustBeLessThanThousand: (collectionName: string) =>
    `WDE0006: Failed to bulk save items into [${collectionName}].\nCannot insert more than 1000 items in one request`,
  updateItemInvalid: () =>
    'WDE0007: Invalid update. Updated object must have a string _id property.',
  invalidArgumentLength: (
    method: string,
    from: number,
    to: number,
    actual: number
  ) =>
    `WDE0008: wixData.${method} expects between ${from} and ${to} arguments, but was called with ${actual}.`,
  aggregateValidations: {
    aggregateInvalid: (collectionName: string, invalidArguments: string[]) =>
      `Failed to perform aggregation on [${collectionName}].\n${invalidArguments.join(
        '\n'
      )}`,
    filterMustBeBuilder: (operatorName: string) =>
      `WDE0011: Invalid ${operatorName} usage. ${operatorName} requires WixDataFilter.`,
    filterIsAlreadySet: (operatorName: string) =>
      `WDE0012: Invalid ${operatorName} usage. Filter is already set.`,
    groupIsAlreadySet: (operatorName: string) =>
      `WDE0013: Invalid ${operatorName} usage. Group is already set.`,
  },
  filterBuilderInvalid: (invalidArguments: string[]) =>
    `Failed to build a filter.\n${invalidArguments.join('\n')}.`,
  groupBuilderInvalid: (invalidArguments: string[]) =>
    `Failed to build group.\n${invalidArguments.join('\n')}.`,
  filterMustBeAnObject: () => 'WDE0016: Filter must be an object.',
  sortBuilderInvalid: (invalidArguments: string[]) =>
    `Failed to build a sort.\n${invalidArguments.join('\n')}.`,
  optionsInvalid: (properties: string[]) =>
    `WDE0018: Options must be an object with one or all of the following boolean properties: ${properties.join(
      ', '
    )}.`,
  referenceOperationParameterError: () =>
    'WDE0019: Reference operation takes a string ID or an object with an ID to be connected.',
  referenceOperationFieldError: (fieldName: string) =>
    `WDE0020: Provided property [${fieldName}] is not a multi-reference field.`,
  invalidReferenceError: () => `WDE0021: Invalid reference`,
  queryValidations: {
    queryInvalid: (collectionName: string, invalidArguments: string[]) =>
      `Failed to perform query on [${collectionName}].\n${invalidArguments.join(
        '\n'
      )}`,
    isNumber: (operatorName: string, specifier: string, operand: any) =>
      `WDE0032: Invalid ${operatorName} parameter [${typeForDisplay(
        operand
      )}]. ${operatorName} parameter must be a ${specifier} number.`,
    isPositiveNumber: (operatorName: string, operand: number) =>
      `WDE0033: Invalid ${operatorName} parameter [${operand}]. ${operatorName} parameter must be a positive number.`,
    isNonNegativeNumber: (operatorName: string, operand: number) =>
      `WDE0034: Invalid ${operatorName} parameter [${operand}]. ${operatorName} parameter must be a non-negative number.`,
    isInteger: (operatorName: string, operand: number) =>
      `WDE0035: Invalid ${operatorName} parameter [${operand}]. ${operatorName} parameter must be an integer.`,
    isNonEmptyString: (operatorName: string) =>
      `WDE0094: Invalid ${operatorName} parameter. ${operatorName} parameter must be non-empty string.`,
    notGreaterThan: (operatorName: string, operand: number, value: number) =>
      `WDE0036: Invalid ${operatorName} parameter [${operand}]. ${operatorName} parameter cannot exceed ${value}.`,
    invalidSkipParameter: (collectionName: string, skipParameter: number) =>
      `WDE0037: Invalid query on [${collectionName}].\nInvalid prev positioned query skip on a negative number ${skipParameter}.`,
    noPrevPage: (collectionName: string) =>
      `WDE0159: Invalid query on [${collectionName}].\nThere is no prev page.`,
    noNextPage: (collectionName: string) =>
      `WDE0165: Invalid query on [${collectionName}].\nThere is no next page.`,
  },
  arityValidations: {
    arityIsZero: (operatorName: string) =>
      `WDE0038: Invalid ${operatorName} usage. ${operatorName} does not take parameters.`,
    arityIsOne: (operatorName: string) =>
      `WDE0039: Invalid ${operatorName} usage. ${operatorName} requires one parameter.`,
    arityIsTwo: (operatorName: string) =>
      `WDE0040: Invalid ${operatorName} usage. ${operatorName} requires two parameters.`,
    arityIsThree: (operatorName: string) =>
      `WDE0041: Invalid ${operatorName} usage. ${operatorName} requires three parameters.`,
    arityIsAtLeastTwo: (operatorName: string) =>
      `WDE0042: Invalid ${operatorName} usage. ${operatorName} requires at least two parameters.`,
    arityIsAtLeastOne: (operatorName: string) =>
      `WDE0043: Invalid ${operatorName} usage. ${operatorName} requires at least one parameter.`,
  },
  filterValidations: {
    typeIsString: (operatorName: string, value: any) =>
      `WDE0044: Invalid ${operatorName} parameter value [${typeForDisplay(
        value
      )}]. ${operatorName} parameter must be a String.`,
    typeIsStringNumberOrDate: (operatorName: string, value: any) =>
      `WDE0045: Invalid ${operatorName} parameter value [${typeForDisplay(
        value
      )}]. Valid ${operatorName} parameter types are String, Number or Date.`,
    sameType: (operatorName: string, first: any, second: any) =>
      `WDE0046: Invalid ${operatorName} parameter values [${typeForDisplay(
        first
      )}] and [${typeForDisplay(
        second
      )}]. Both parameters must be of the same type.`,
    typeIsStringNumberOrDateForAll: (operatorName: string) =>
      `WDE0047: Invalid ${operatorName} usage. ${operatorName} supports only Number, String or Date items.`,
    validFieldName: (operatorName: string, field: string) =>
      `WDE0048: Invalid ${operatorName} field value [${typeForDisplay(
        field
      )}]. ${operatorName} field must be a String.`,
    isInstanceOfSameClass: (
      operatorName: string,
      constructorName: string,
      obj: any
    ) =>
      `WDE0049: Invalid ${operatorName} parameter [${typeForDisplay(
        obj
      )}]. ${operatorName} expects ${constructorName} only.`,
    isForCollection: (
      operatorName: string,
      constructorName: string,
      collectionName: string
    ) =>
      `WDE0050: Invalid ${operatorName} parameter query for [${collectionName}]. ${operatorName} accepts ${constructorName} for the same collection only.`,
  },
  filterTreeValidations: {
    objectType: (operatorName: string, value: any) =>
      `WDE0056: ${operatorName} should be an Object. Got ${stringify(
        value
      )} instead`,
    arrayType: (operatorName: string, value: any) =>
      `WDE0057: ${operatorName} should be an Array. Got ${stringify(
        value
      )} instead`,
    arrayLength: (operatorName: string, expectedLength: number, value: any[]) =>
      `WDE0057: ${stringify(value)}.length is ${
        value.length
      }. ${operatorName} Array should have length ${expectedLength}`,
    comparisonOperatorType: (operatorName: string, value: any) =>
      `WDE0058: ${operatorName} should be a Date, Number, or String. Got ${stringify(
        value
      )} instead`,
    stringOperatorType: (operatorName: string, value: any) =>
      `WDE0059: ${operatorName} should be a String. Got ${stringify(
        value
      )} instead`,
    setOperatorItems: (operatorName: string, value: any) =>
      `WDE0060: ${operatorName} Array should only contain values of types Date, Number, and String. Got ${stringify(
        value
      )} instead`,
    inOperatorItems: (value: any) =>
      `WDE0061: $in Array should have length 2, and match [String, Number]. Got ${stringify(
        value
      )} instead`,
    matchesOperatorRequiredProperty: (propertyName: string, value: any) =>
      `WDE0062: $matches value ${stringify(
        value
      )} does not have property ${propertyName}`,
    matchesOperatorIgnoreCase: (value: any) =>
      `WDE0063: $matches.ignoreCase should equal true. Got ${stringify(
        value
      )} instead`,
    matchesOperatorSpecItems: (value: any) =>
      `WDE0064: $matches.spec Array values should be either {"type":"anyOf","value":" -"} or {"type":"literal","value":String}. Got ${stringify(
        value
      )} instead`,
    regexNotAllowed: () => 'WDE0070: $regex keyword is not allowed.',
  },
  sortValidations: {
    typeIsStringOrArrayOfStrings: (
      operatorName: string,
      effectiveArgs: any[]
    ) =>
      `WDE0051: Invalid ${operatorName} parameters [${effectiveArgs.map(
        typeForDisplay
      )}]. Valid ${operatorName} values are String, Array of String or varargs String.`,
  },
  orderByValidations: {
    sortModelType: (value: any) =>
      `WDE0065: Sort Model should be an Array. Got ${stringify(value)} instead`,
    sortModelItemType: (value: any) =>
      `WDE0066: Sort Model Array should contain values of type Object only. Got ${stringify(
        value
      )} instead`,
    sortModelItem: (value: any) =>
      `WDE0067: Sort Model Array items should have a single property with value "asc" or "desc". Got ${stringify(
        value
      )} instead`,
  },
  internalError: (message: string) =>
    `WDE0053: Internal wixData error: ${message}`,
  serverInvalidResponse: (message: string) =>
    'WDE0055: Failed to parse server response.' +
    (message ? ` ${message}` : ''),
  itemDoesNotExist: (id: string, collectionName: string) =>
    `WDE0073: Item [${id}] does not exist in collection [${collectionName}].`,
  cursorPagingDoesNotSupportSkip: () =>
    'WDE0080: Skip is not supported in cursor paging.',
  payloadIsTooLarge: () => 'WDE0109: Payload is too large.',
}

function stringify(obj: any) {
  return safeJsonStringify(obj)
}

export const codes = {
  ItemDoesNotExist: 'WD_ITEM_DOES_NOT_EXIST',
  ItemAlreadyExists: 'WD_ITEM_ALREADY_EXISTS',
  SiteInTemplateMode: 'WD_SITE_IN_TEMPLATE_MODE',
  UnknownError: 'WD_UNKNOWN_ERROR',
  ValidationError: 'WD_VALIDATION_ERROR',
  CollectionDeleted: 'WD_COLLECTION_DELETED',
  SchemaDoesNotExist: 'WD_SCHEMA_DOES_NOT_EXIST',
  PermissionDenied: 'WD_PERMISSION_DENIED',
  BadRequest: 'WD_BAD_REQUEST',
  Unauthorized: 'WD_UNAUTHORIZED',
  TooManyRequests: 'WD_TOO_MANY_REQUESTS',
  RequestTimedOut: 'WD_REQUEST_TIMED_OUT',
  QuotaExceeded: 'WD_DATABASE_QUOTA_EXCEEDED',
  QueryExecutionError: 'WD_QUERY_EXECUTION_ERROR',
}

export function wixDataError(message: string, code: string, details?: any) {
  return errorWithCode(message, code, details)
}

export function validationError(message: string) {
  return errorWithCode(message, codes.ValidationError)
}

function errorWithCode(message: string, code: string, details?: any) {
  const ErrorConstructor =
    code && code !== codes.UnknownError ? WixDataError : Error
  // @ts-ignore-next-line
  const error = new ErrorConstructor(message)
  // @ts-ignore-next-line
  error.code = code
  if (details) {
    // @ts-ignore-next-line
    error.details = details
  }
  return error
}

class WixDataError extends Error {
  name = 'Error'

  // marker for user error
  errorGroup = 'User'

  constructor(message: string) {
    super(message)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, WixDataError)
    }
  }
}

export class BulkError extends Error {
  constructor(
    public message: string,
    public code: string,
    public item: any,
    public name: string,
    public originalIndex: number
  ) {
    super(message)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BulkError)
    }
  }
}
